import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFirestore, collection, getDocs, orderBy, query } from 'firebase/firestore';
import { ArrowPathIcon, PlayIcon, QueueListIcon } from '@heroicons/react/24/outline';
import {
  REQUIRED_LABELS,
  hasValidBox,
  isActiveLabelingWork,
  orderBatchFirst,
} from './labelStatus';

export default function ViewStimulusQueue() {
  const navigate = useNavigate();
  const location = useLocation();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const snapshot = await getDocs(query(collection(db, 'images'), orderBy('hash')));
      const stimuli = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      const unlabeled = stimuli
        .filter((stimulus) => isActiveLabelingWork(stimulus))
        .map((stimulus) => ({ ...stimulus, hash: stimulus.hash || stimulus.id }));

      setQueue(unlabeled);
      setError(null);
    } catch (err) {
      console.error('Error loading labeling queue:', err);
      setError('Failed to load labeling queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // A just-finished upload batch can hand us its hashes so those stimuli are
  // labeled first. The batch travels with every queue-mode navigation so the
  // detail view keeps the same ordering.
  const batchHashes = useMemo(() => (
    Array.isArray(location.state?.batchHashes) ? location.state.batchHashes : []
  ), [location.state]);

  const openQueueItem = useCallback((hash) => {
    navigate(`/stimuli/${hash}?mode=queue`, { state: { batchHashes } });
  }, [navigate, batchHashes]);

  const orderedQueue = useMemo(
    () => orderBatchFirst(queue, batchHashes, (item) => item.hash),
    [queue, batchHashes],
  );

  const batchItemCount = useMemo(() => {
    if (batchHashes.length === 0) {
      return 0;
    }
    const batchSet = new Set(batchHashes);
    return queue.filter((item) => batchSet.has(item.hash)).length;
  }, [queue, batchHashes]);

  const lastQueueHash = useMemo(() => {
    try {
      return localStorage.getItem('vasi_admin_last_queue_hash');
    } catch {
      return null;
    }
  }, []);

  const resumeItem = useMemo(() => (
    lastQueueHash ? queue.find((item) => item.hash === lastQueueHash) : null
  ), [queue, lastQueueHash]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
        <button
          type="button"
          onClick={fetchQueue}
          className="mt-4 inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          <ArrowPathIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Stimulus Label Queue</h1>
          <p className="mt-2 text-sm text-gray-700">
            Focused labeling mode for Tanner stimuli missing one or more required AOI labels.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex sm:items-center sm:gap-2">
          <button
            type="button"
            onClick={fetchQueue}
            className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <ArrowPathIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
            Refresh
          </button>
          {orderedQueue.length > 0 && (
            <button
              type="button"
              onClick={() => openQueueItem(orderedQueue[0].hash)}
              className="inline-flex items-center gap-x-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500"
            >
              <PlayIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
              Start Queue
            </button>
          )}
          {resumeItem && (
            <button
              type="button"
              onClick={() => openQueueItem(resumeItem.hash)}
              className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
            >
              <QueueListIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
              Resume Last
            </button>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-lg bg-gray-50 p-4">
        <p className="text-sm text-gray-700">
          Remaining unlabeled Tanner stimuli: <span className="font-semibold text-gray-900">{queue.length}</span>
        </p>
      </div>

      {batchItemCount > 0 && (
        <div className="mt-4 rounded-md border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900">
          {batchItemCount} stimul{batchItemCount === 1 ? 'us' : 'i'} from your recent upload batch {batchItemCount === 1 ? 'is' : 'are'} listed first.
        </div>
      )}

      {queue.length === 0 ? (
        <div className="mt-6 rounded-md bg-emerald-50 p-4 text-emerald-800">
          Queue is empty. All required labels are complete.
        </div>
      ) : (
        <div className="mt-8 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Hash</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Gender</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Tanner</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Complete Labels</th>
                <th scope="col" className="relative py-3.5 pl-3 pr-4">
                  <span className="sr-only">Open</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {orderedQueue.map((stimulus) => {
                const completeCount = REQUIRED_LABELS.filter((name) => hasValidBox(stimulus?.labels?.[name])).length;
                const shortHash = stimulus.hash
                  ? `${stimulus.hash.slice(0, 8)}...${stimulus.hash.slice(-8)}`
                  : stimulus.id;
                return (
                  <tr key={stimulus.id}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-mono text-gray-900">{shortHash}</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">{stimulus.gender || 'N/A'}</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">{stimulus.tanner || 'N/A'}</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">{completeCount}/{REQUIRED_LABELS.length}</td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium">
                      <button
                        type="button"
                        onClick={() => openQueueItem(stimulus.hash)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
